import { useCallback, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { BarChart3 } from "lucide-react";

import { Card, CardContent, Skeleton } from "@kuzco/web/ui";

import type { CohortSizeDays } from "../../../components/admin/usage/RetentionCohortSizePicker";
import type { UsageTimezone } from "../../../components/admin/usage/timezone";
import type {
  Granularity,
  UsageRange,
} from "../../../server/routers/admin-usage.helpers";
import { RetentionCohortSizePicker } from "../../../components/admin/usage/RetentionCohortSizePicker";
import { RetentionTable } from "../../../components/admin/usage/RetentionTable";
import { UsageCumulativeUsersChart } from "../../../components/admin/usage/UsageCumulativeUsersChart";
import { UsageGranularityPicker } from "../../../components/admin/usage/UsageGranularityPicker";
import { UsagePieChart } from "../../../components/admin/usage/UsagePieChart";
import { UsageRangePicker } from "../../../components/admin/usage/UsageRangePicker";
import { UsageRefreshButton } from "../../../components/admin/usage/UsageRefreshButton";
import { UsageStatsGrid } from "../../../components/admin/usage/UsageStatsGrid";
import { UsageTimeseriesChart } from "../../../components/admin/usage/UsageTimeseriesChart";
import { UsageTimezonePicker } from "../../../components/admin/usage/UsageTimezonePicker";
import { nubankTrpc } from "../../../trpc";

export const Route = createFileRoute("/_authed/admin/usage-stats")({
  component: UsageStatsPage,
  head: () => ({
    meta: [{ title: "Usage Stats | NBDC" }],
  }),
});

function normalizeLabel(label: string): string {
  if (label.toLowerCase() === "unknown") return "unknown";
  if (label.includes("nubank-")) return "nubank-bdc-coder";
  return label;
}

function mergeSlices(
  slices: { label: string; requests: number }[],
): { label: string; value: number }[] {
  const map = new Map<string, number>();
  for (const s of slices) {
    const key = normalizeLabel(s.label);
    map.set(key, (map.get(key) ?? 0) + s.requests);
  }
  return Array.from(map, ([label, value]) => ({ label, value }));
}

function UsageStatsPage() {
  const [range, setRangeRaw] = useState<UsageRange>("today");
  const [granularity, setGranularity] = useState<Granularity>("1h");

  const setRange = useCallback((next: UsageRange) => {
    setRangeRaw(next);
    if (next === "month" || next === "all-time") {
      setGranularity("1d");
    } else {
      setGranularity("1h");
    }
  }, []);
  const [timezone, setTimezone] = useState<UsageTimezone>("local");
  const [cohortSize, setCohortSize] = useState<CohortSizeDays>(7);

  const utils = nubankTrpc.useUtils();

  const summary = nubankTrpc.admin.usage.getSummary.useQuery({ range });
  const timeseries = nubankTrpc.admin.usage.getTimeseries.useQuery({
    range,
    granularity,
    timezone,
  });
  const modelBreakdown = nubankTrpc.admin.usage.getModelBreakdown.useQuery({
    range,
  });
  const toolBreakdown = nubankTrpc.admin.usage.getToolBreakdown.useQuery({
    range,
  });
  const providerBreakdown =
    nubankTrpc.admin.usage.getProviderBreakdown.useQuery({ range });
  const cumulative = nubankTrpc.admin.usage.getCumulativeUsers.useQuery({
    timezone,
  });
  const retention = nubankTrpc.admin.usage.getRetention.useQuery({
    cohortSizeDays: cohortSize,
    timezone,
  });

  const modelSlices = useMemo(
    () => mergeSlices(modelBreakdown.data?.slices ?? []),
    [modelBreakdown.data],
  );
  const toolSlices = useMemo(
    () => mergeSlices(toolBreakdown.data?.slices ?? []),
    [toolBreakdown.data],
  );
  const providerSlices = useMemo(
    () => mergeSlices(providerBreakdown.data?.slices ?? []),
    [providerBreakdown.data],
  );
  const isRefreshing =
    summary.isFetching ||
    timeseries.isFetching ||
    modelBreakdown.isFetching ||
    toolBreakdown.isFetching ||
    providerBreakdown.isFetching ||
    cumulative.isFetching ||
    retention.isFetching;

  const handleRefresh = useCallback(() => {
    void utils.admin.usage.invalidate();
  }, [utils]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <BarChart3 className="h-6 w-6 text-muted-foreground" />
        <h1 className="text-3xl font-semibold tracking-tight">Usage stats</h1>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <UsageRangePicker value={range} onChange={setRange} />
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            Bucket
          </span>
          <UsageGranularityPicker
            value={granularity}
            onChange={setGranularity}
          />
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            Timestamps
          </span>
          <UsageTimezonePicker value={timezone} onChange={setTimezone} />
          <UsageRefreshButton
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
          />
        </div>
      </div>

      {/* Summary */}
      {summary.isPending ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[76px] w-full" />
          ))}
        </div>
      ) : summary.data ? (
        <UsageStatsGrid
          totalRequests={summary.data.requestCount}
          activeUsers={summary.data.activeUsers}
          totalTokens={summary.data.totalTokens}
          inputTokens={summary.data.inputTokens}
          outputTokens={summary.data.outputTokens}
          reasoningTokens={summary.data.reasoningTokens}
          totalCost={summary.data.totalCost}
        />
      ) : null}

      {/* Pies */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {modelBreakdown.isPending ? (
          <Skeleton className="h-[340px] w-full" />
        ) : (
          <UsagePieChart title="Models" data={modelSlices} />
        )}
        {toolBreakdown.isPending ? (
          <Skeleton className="h-[340px] w-full" />
        ) : (
          <UsagePieChart title="Tools" data={toolSlices} />
        )}
        {providerBreakdown.isPending ? (
          <Skeleton className="h-[340px] w-full" />
        ) : (
          <UsagePieChart title="Providers" data={providerSlices} />
        )}
      </div>

      {/* Timeseries */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {timeseries.isPending ? (
          <>
            <Skeleton className="h-[260px] w-full" />
            <Skeleton className="h-[260px] w-full" />
            <Skeleton className="h-[260px] w-full" />
            <Skeleton className="h-[260px] w-full" />
          </>
        ) : (
          <>
            <UsageTimeseriesChart
              title="Requests"
              data={timeseries.data?.buckets ?? []}
              granularity={granularity}
              timezone={timezone}
              variant="requests"
            />
            <UsageTimeseriesChart
              title="Active users"
              data={timeseries.data?.buckets ?? []}
              granularity={granularity}
              timezone={timezone}
              variant="activeUsers"
            />
            <UsageTimeseriesChart
              title="Tokens"
              data={timeseries.data?.buckets ?? []}
              granularity={granularity}
              timezone={timezone}
              variant="tokens"
            />
            <UsageTimeseriesChart
              title="Cost"
              data={timeseries.data?.buckets ?? []}
              granularity={granularity}
              timezone={timezone}
              variant="cost"
            />
          </>
        )}
      </div>

      {/* Cumulative users */}
      {cumulative.isPending ? (
        <Skeleton className="h-[300px] w-full" />
      ) : cumulative.data ? (
        <UsageCumulativeUsersChart
          title="Cumulative users"
          data={cumulative.data.points}
          totals={cumulative.data.totals}
        />
      ) : null}

      {/* Retention */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-medium">Retention (request-based)</h2>
          <RetentionCohortSizePicker
            value={cohortSize}
            onChange={setCohortSize}
          />
        </div>
        {retention.isPending ? (
          <Skeleton className="h-[260px] w-full" />
        ) : retention.data ? (
          <RetentionTable
            cohorts={retention.data.cohorts}
            cohortSizeDays={retention.data.cohortSizeDays}
          />
        ) : (
          <Card>
            <CardContent className="p-6 text-sm text-muted-foreground">
              No retention data yet.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
